import { useState } from 'react';
import axios from 'axios';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { config } from '@/config';
import { StepUp } from './stepUp';
import { CeremonyDismissed, CeremonyUnsupported, getAssertion } from '@/lib/webauthn';

/**
 * Re-prove presence, by whichever rung of the ladder the server offered.
 *
 * The server decides which: a key when the account holds one usable at this origin, a password
 * when it does not. Offering the password when a key is available would let the weaker factor
 * undercut the stronger one, so this never chooses — it renders what it was given.
 */
/**
 * ⚠ **Satisfied and dismissed are separate callbacks, deliberately.** They were a single
 * callback, and a caller that re-ran its action on it — which is the entire point of stepping
 * up — re-ran it on *cancel* too: the retry drew a fresh 403, which reopened this dialog, so
 * Esc and Cancel did nothing but flicker. One callback made the caller guess, and there was
 * nothing to guess from. Two props cannot be got wrong silently.
 */
export function StepUpChallenge({
  challenge,
  onSatisfied,
  onCancel,
}: {
  challenge: StepUp;
  /** The proof landed. The caller should re-run whatever it was refused. */
  onSatisfied: () => void;
  /** The user backed out. The caller must NOT re-run anything. */
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const runCeremony = () => {
    if (!challenge.challenge || !challenge.challengeId) return;
    setError(undefined);
    setPending(true);
    getAssertion(challenge.challenge)
      .then((credential) =>
        axios.post(
          `${config.backendApi}/api/auth/step-up/finish`,
          { challengeId: challenge.challengeId, credential },
          { withCredentials: true },
        ),
      )
      .then(
        () => { onSatisfied(); },
        (e: unknown) => {
          if (e instanceof CeremonyUnsupported) console.error('Step-up refused', e.cause);
          setError(
            e instanceof CeremonyDismissed
              ? 'That prompt was dismissed.'
              : 'That key could not be verified.',
          );
        },
      )
      .finally(() => { setPending(false); });
  };

  const submitPassword = () => {
    setError(undefined);
    setPending(true);
    void axios
      .post(`${config.backendApi}/api/auth/step-up/password`, { password }, { withCredentials: true })
      .then(
        () => { setPassword(''); onSatisfied(); },
        () => { setError('That password was not accepted.'); },
      )
      .finally(() => { setPending(false); });
  };

  const usesKey = challenge.stepUpRequired === 'step_up';

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Confirm it is you</DialogTitle>
          <DialogDescription>
            {usesKey
              ? 'This change needs a fresh check. Use your security key or passkey.'
              : 'This change needs a fresh check. Enter your password.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {usesKey ? (
          <Button onClick={runCeremony} disabled={pending} data-testid="stepup-key">
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {pending ? 'Waiting…' : 'Use my key'}
          </Button>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="stepup-password">Password</Label>
            <Input
              id="stepup-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              disabled={pending}
              onChange={(e) => { setPassword(e.target.value); }}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>Cancel</Button>
          {!usesKey && (
            <Button onClick={submitPassword} disabled={pending || password === ''} data-testid="stepup-password-submit">
              {pending ? 'Checking…' : 'Confirm'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
